import { ref } from "vue";
import { defineStore } from "pinia";
import {
  getDepartmentTree,
  getDepartments,
  editDepartment,
  checkDepartmentRules,
  getStaff
} from "../api/departments";
import { getMyDepartments } from "@/api/exchange";
import { Department, HoursLimitEmployment, ScheduleRules } from "@/types";
import { useToast } from "vue-toast-notification";
import { Staff } from "@/types";

const toast = useToast({ position: "bottom-right" });

export const useDepartmentStore = defineStore("department", () => {
  // Состояния
  const departments = ref<Department[]>([]);
  const departmentsAll = ref<Department[]>([]);
  const myDepartments = ref<Department[]>([]);
  const myAllDepartments = ref<Department[]>([]);
  const departmentsTree = ref<any>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const warnedEmployee = ref<string[]>([])
  const hoursLimitViolations = ref<HoursLimitEmployment[]>([])
  const staff = ref<Staff[]>([])

  // Функции для работы с подразделениями

  async function fetchDepartmentsTree() {
    try {
      const res = await getDepartmentTree();
      departmentsTree.value = res.data.body;
    } catch (err: any) {
      return err;
    }
  }
  async function fetchDepartments(params?: any) {
    const data = params || {};
    data.only_leafs = true;
    try {
      const res = await getDepartments(data);
      departments.value = res.data.body;
    } catch (err: any) {
      return err;
    }
  }

  async function fetchAllDepartments() {
    const data = {};
    try {
      const res = await getDepartments(data);
      departmentsAll.value = res.data.body;
    } catch (err: any) {
      return err;
    }
  }

  async function fetchMyAllDepartments(params?: any) {
    const data = params || {};
    try {
      const res = await getMyDepartments(data);
      if (res.data) myAllDepartments.value = res.data?.body;
    } catch (err: any) {
      return err;
    }
  }

  async function fetchMyDepartments(params?: any) {
    const data = params || {};
    data.only_leafs = true;
    try {
      const res = await getMyDepartments(data);
      if (res.data) myDepartments.value = res.data?.body;
    } catch (err: any) {
      return err;
    }
  }

  async function changeDepartment(id: string, data: any) {
    try {
      const res = await editDepartment(id, data);
      if (res.data) {
        toast.success(res.data.msg);
        fetchAllDepartments();
      }
    } catch (err: any) {
      return err;
    }
  }

  async function fetchDepartmentRules(
    id: string,
    params: { month: string; year: string },
    shouldApply: () => boolean = () => true,
  ): Promise<ScheduleRules | undefined> {
      if (shouldApply()) {
        warnedEmployee.value = [];
        hoursLimitViolations.value = [];
      }
      try {
        const res = await checkDepartmentRules(id, params);
        if (res.data?.status !== "error" && res.data?.body) {
          const result: ScheduleRules = res.data.body;
          if (shouldApply()) {
            warnedEmployee.value = result.rule_42_hours?.employments?.map(item => item.employment_id) ?? [];
            hoursLimitViolations.value = result.hours_limit_rule?.employments?.filter(item => !item.is_valid) ?? [];
          }
          return result;
        }
      } catch {
        // Ошибку показывает HTTP-клиент; вызывающий код не должен принять её за результат checker.
        return undefined;
      }
  }

  async function fetchStaff(id:string) {
      try {
        const res = await getStaff(id);
        if(res.data) {
          staff.value = res.data.body.data
        }
      } catch (err: any) {
        return err;
      }
  }

  return {
    // Состояния
    departments,
    departmentsAll,
    myDepartments,
    myAllDepartments,
    departmentsTree,
    loading,
    error,
    warnedEmployee,
    hoursLimitViolations,
    staff,

    // Функции
    fetchDepartmentsTree,
    fetchDepartments,
    fetchAllDepartments,
    fetchMyDepartments,
    fetchMyAllDepartments,
    changeDepartment,
    fetchDepartmentRules,
    fetchStaff
  };
});
